import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import '@/App.css';

// Pages
import LandingPage from '@/pages/LandingPage';
import AdminLogin from '@/pages/AdminLogin';
import AdminDashboard from '@/pages/AdminDashboard';
import RoomDetails from '@/pages/RoomDetails';
import RoomTypeDetails from '@/pages/RoomTypeDetails';
import MultiRoomBooking from '@/pages/MultiRoomBooking';
import BookingSuccess from '@/pages/BookingSuccess';
import BookingCancel from '@/pages/BookingCancel';
import GuestPortalLogin from '@/pages/GuestPortalLogin';
import GuestDashboard from '@/pages/GuestDashboard';
import GuestPaymentSuccess from '@/pages/GuestPaymentSuccess';
import NexiDemoPayment from '@/pages/NexiDemoPayment';

// Components
import { Toaster } from '@/components/ui/sonner';
import ProtectedRoute, { PublicRoute } from '@/components/ProtectedRoute';
import ErrorBoundary from '@/components/ErrorBoundary';
import GoogleAnalytics from '@/components/GoogleAnalytics';

// Context
import { AuthProvider } from '@/contexts/AuthContext';

// API Base URL Export (for backward compatibility with existing code)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/**
 * Main App Component
 *
 * Changes from original:
 * 1. Removed axios interceptor (now in api.js service)
 * 2. Removed local auth state (now in AuthContext)
 * 3. Added AuthProvider wrapper
 * 4. Using ProtectedRoute for admin routes
 * 5. Using PublicRoute for login (redirects if already authenticated)
 * 6. BrowserRouter wraps AuthProvider so useNavigate works in context
 * 7. Added ErrorBoundary to catch and display errors gracefully
 */
function App() {
  return (
    <ErrorBoundary>
      <div className="App">
        <GoogleAnalytics />
        <BrowserRouter>
          <AuthProvider>
            <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/room/:roomId" element={<RoomDetails />} />
            <Route path="/room-type/:roomTypeId" element={<RoomTypeDetails />} />
            <Route path="/booking-success" element={<BookingSuccess />} />
            <Route path="/booking-cancel" element={<BookingCancel />} />
            <Route path="/nexi-demo-payment" element={<NexiDemoPayment />} />

            {/* Guest Portal Routes */}
            <Route path="/guest/login" element={<GuestPortalLogin />} />
            <Route path="/guest/dashboard" element={<GuestDashboard />} />
            <Route path="/guest-payment-success" element={<GuestPaymentSuccess />} />

            {/* Admin Login - Public Route (redirects to dashboard if authenticated) */}
            <Route
              path="/admin/login"
              element={
                <PublicRoute>
                  <AdminLogin />
                </PublicRoute>
              }
            />

            {/* Admin Dashboard - Protected Route (requires authentication) */}
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            {/* Multi-room booking - Currently disabled but kept for future use */}
            {/*
            <Route path="/cart" element={<MultiRoomBooking />} />
            */}
          </Routes>

          {/* Toast notifications */}
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  </ErrorBoundary>
  );
}

export default App;
